import React, { type JSX } from 'react';
import { CustomModal, CustomTransComponent } from '../../../components';
import { useConfirm } from '@fiji/common/src/hooks';
import { ModalActions } from '../../../components/CustomModal';
import { FormHelperText, Stack, Typography } from '@mui/material';
import { WIDGET_DESCRIPTION_LIMIT, WIDGET_NAME_LIMIT } from '@fiji/common/src/constants';
import { useHandleWidgetListActions } from '../common';
import { resetRenameWidgetModal, setRenameWidgetPayload } from '@fiji/common/src/features/widgetManagement/widgetSlice';
import { useAppDispatch } from '@fiji/common/src/app/store';
import LanguageTextField from 'components/FormElements/LanguageTextField';
import { useTextFieldError } from 'hooks';

const getStringValue = (value: unknown): string => (typeof value === 'string' ? value : '');

export const RenameModal = React.forwardRef((props: any, ref): JSX.Element => {
    const dispatch = useAppDispatch();
    const { hasTextError, makeErrorHandler } = useTextFieldError();
    const {
        payload,
        handleRenameModalAction: handleModalAction,
        isLoading,
        handleRenameSave: handleSave,
    } = useHandleWidgetListActions('renameWidgetModal', (data: any) => onClick(data));

    React.useImperativeHandle(
        ref,
        () => ({
            handleModalAction: (action: boolean, widgetData: any): void => handleModalAction(action, widgetData),
        }),
        []
    );
    const { data, isVisible, onCancel, onClick, onConfirm } = useConfirm((modalData: any): any =>
        handleSave(modalData, onCancel)
    );

    const { name: payloadName, description: payloadDescription } = payload ?? {};
    const normalizedPayload = {
        name: getStringValue(payloadName),
        description: getStringValue(payloadDescription),
    };
    const { name, description } = normalizedPayload;
    const isSaveDisabled =
        isLoading || !name || hasTextError || (name === data?.name && description === data?.description);

    const newWidgetModalActions: ModalActions[] = [
        {
            key: '#creationcancel',
            label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
            close: true,
            variant: 'outlined',
            handleClick: (): void => {
                dispatch(resetRenameWidgetModal());
                onCancel();
            },
        },
        {
            key: '#creationsave',
            label: <CustomTransComponent translationKey={'COMMON:SAVE_LABEL'} />,
            variant: 'contained',
            disabled: isSaveDisabled,
            handleClick: (): void => {
                onConfirm();
            },
        },
    ];

    return (
        <CustomModal
            key="#new!@1313d"
            type="primary"
            isLoading={isLoading}
            actionsDivider
            header={<CustomTransComponent translationKey={'WIDGETS:RENAME_WIDGET_MODAL.RENAME_WIDGET'} />}
            actions={newWidgetModalActions}
            isOpen={isVisible}
            onClose={onCancel}
        >
            <Typography variant="subtitle1" fontWeight={'600'} my={2}>
                <CustomTransComponent translationKey={'WIDGETS:CLONE_MODAL_HEADER'} />
            </Typography>
            <LanguageTextField
                fullWidth
                id="widget-name"
                name="name"
                label={<CustomTransComponent translationKey={'COMMON:NAME'} />}
                inputProps={{ maxLength: WIDGET_NAME_LIMIT }}
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    dispatch(setRenameWidgetPayload({ key: 'name', value: e.target.value }));
                }}
                variant="filled"
                className="margin-top-18"
                required
                treatLeadingWhitespaceAsError
                handleTextError={makeErrorHandler('name')}
            />
            <Stack direction={'row'} justifyContent={'space-between'} px={2}>
                <FormHelperText>
                    <CustomTransComponent translationKey={'COMMON:REQUIRED_LABEL'} />
                </FormHelperText>
                <FormHelperText>{`${name.length}/${WIDGET_NAME_LIMIT}`}</FormHelperText>
            </Stack>
            <LanguageTextField
                id="outlined-password-input"
                fullWidth
                variant="filled"
                name="description"
                inputProps={{ maxLength: WIDGET_DESCRIPTION_LIMIT }}
                value={description}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    dispatch(setRenameWidgetPayload({ key: 'description', value: e.target.value }));
                }}
                label={<CustomTransComponent translationKey={'COMMON:DESCRIPTION'} />}
                className="margin-top-18"
                treatLeadingWhitespaceAsError
                handleTextError={makeErrorHandler('description')}
            />
            <FormHelperText className="text-align-end padding-x-16">
                {`${description.length}/${WIDGET_DESCRIPTION_LIMIT}`}
            </FormHelperText>
        </CustomModal>
    );
});
